import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { Suspense, lazy } from "react";
import { Loader2 } from "lucide-react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ScrollToTopOnNavigate } from "@/components/ScrollToTopOnNavigate";
import { ArabicCursor } from "@/components/ArabicCursor";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const ToolPage = lazy(() => import("./pages/ToolPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ApiPage = lazy(() => import("./pages/ApiPage"));
const DevelopersPage = lazy(() => import("./pages/DevelopersPage"));
const GrammarPage = lazy(() => import("./pages/GrammarPage"));
const GrammarDetailPage = lazy(() => import("./pages/GrammarDetailPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const EmailVerificationPage = lazy(() => import("./pages/EmailVerificationPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const DocsPage = lazy(() => import("./pages/DocsPage"));
const TutorialsPage = lazy(() => import("./pages/TutorialsPage"));
const PaymentHistoryPage = lazy(() => import("./pages/PaymentHistoryPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const BuyCreditsPage = lazy(() => import("./pages/BuyCreditsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <ArabicCursor />
            <ScrollToTopOnNavigate />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/tool" element={<ToolPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/api" element={<ApiPage />} />
              <Route path="/developers" element={<DevelopersPage />} />
              <Route path="/grammar" element={<GrammarPage />} />
              <Route path="/grammar/:id" element={<GrammarDetailPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:id" element={<BlogDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/tutorials" element={<TutorialsPage />} />
              <Route path="/payment-history" element={<PaymentHistoryPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/buy-credits" element={<BuyCreditsPage />} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
